import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'

import GokuDataGrid from '@/components/GokuDataGrid'
import SubHeaders from '@/components/Headers/SubHeaders'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'

import { IPermissionProps } from '@/types/shared'
import { paths } from '@/utils/path'
import {
    getServiceUsageTemplateAPIs,
    deleteServiceUsageTemplateAPI,
} from '../../services'

import AddIcons from '@/icons/AddIcons'
import EditIcons from '@/icons/EditIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'

import columns from './columns'
import { ServiceUsageTemplateAPI } from '../../types'
import { Title } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const List: React.FC<IPermissionProps> = () => {
    const { t } = useTranslation()
    const { setConfirmation } = useConfirmation()
    const { setModal } = useModal()
    const { refetchCurrent } = useDataGridRefetch({
        reactQueryKey: 'api-usage-templates',
    })

    const { mutateAsync: deleteTemplate, isPending: isDeleting } =
        useMutationWithHandler(
            {
                mutationFn: deleteServiceUsageTemplateAPI,
            },
            {
                reactQueryKeys: QUERY_KEYS.GENERIC,
                successMessage: t('delete-success'),
                onSuccess: () => {
                    refetchCurrent()
                },
            }
        )

    const handleEdit = (template: ServiceUsageTemplateAPI) => {
        setModal('APIUsageTemplateModals/CreateEditModal/index', {
            template,
            mode: 'edit',
            onSuccess: refetchCurrent,
        })
    }

    const handleDelete = (template: ServiceUsageTemplateAPI) => {
        setConfirmation({
            open: true,
            title: t('confirm-delete'),
            description: t('confirm-delete-api-template'),
            onConfirm: () =>
                deleteTemplate(template.service_usage_template_api_id),
        })
    }

    const handleCreate = () => {
        setModal('APIUsageTemplateModals/CreateEditModal/index', {
            mode: 'create',
            onSuccess: refetchCurrent,
        })
    }

    const dataGridActions = [
        {
            onClick: handleEdit,
            title: t('edit'),
            color: 'info' as const,
            icon: <EditIcons />,
        },
        {
            onClick: handleDelete,
            title: t('delete'),
            color: 'error' as const,
            icon: <DeleteOutlineIcons />,
            disabled: () => isDeleting,
        },
    ]

    return (
        <>
            <SubHeaders
                actions={[
                    {
                        icon: <AddIcons />,
                        label: t('create-api-template'),
                        variant: 'contained',
                        onClick: handleCreate,
                    },
                ]}
                breadcrumbs={[
                    {
                        path: paths.apiUsageTemplates,
                        title: t('api-usage-templates'),
                    },
                ]}
            />

            <div>
                <GokuDataGrid
                    apiCall={getServiceUsageTemplateAPIs}
                    reactQueryKey="api-usage-templates"
                    columns={columns}
                    dataGridProps={{
                        getRowId: (row) => row?.service_usage_template_api_id,
                    }}
                    actions={dataGridActions}
                    searchPlaceholder={t('search-api-templates')}
                    filterDefaultValues={{
                        method: '',
                        active: '',
                        template_id: '',
                    }}
                    options={{
                        pageSize: 10,
                        title: (
                            <Title
                                title={t('api-usage-templates-management')}
                            />
                        ),
                    }}
                />
            </div>
        </>
    )
}

export default List
